import React, { useState, useEffect } from "react"
import { AgCharts } from "ag-charts-react"
import "../../Styles/Overviewstyling.css"
import UseallOverview from "../Componentslogic/UseAlloverview"
import { db } from "../../Firebase/config"
import { collection, query, where, getDocs } from "firebase/firestore"
export const ChartExample = () => {
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  )
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0])

  const [selectedUser, setSelectedUser] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [totalSpent, setTotalSpent] = useState(0)
  const { data: reservations, loading: ordersLoading } = UseallOverview(
    "Orders",
    startDate,
    endDate
  )

  const [statistics, setStatistics] = useState({
    totalOrders: 0,
    avgOrderPrice: 0,
    bestSeller: "None",
    userWithMostOrders: "N/A",
    userWithHighestSpending: "N/A",
  })

  const [chartOptions, setChartOptions] = useState({
    data: [],
    series: [
      {
        type: "line",
        xKey: "hour",
        yKey: "count",
        marker: { enabled: true, size: 5 },
        tooltip: {
          renderer: ({ datum }) => {
            return {
              title: `Hour: ${datum.hour}`,
              content: `Orders: ${datum.count}`,
            }
          },
        },
      },
    ],
  })

  const handleUserClick = async (phone) => {
    if (!phone || phone === "N/A") return

    try {
      const usersRef = collection(db, "users")
      const q = query(usersRef, where("phone", "==", phone))
      const querySnapshot = await getDocs(q)

      let userData = { name: "Unknown", phone: phone } // Default data
      if (!querySnapshot.empty) {
        userData = querySnapshot.docs[0].data()
      }

      // ✅ Calculate total spent by this user
      const userOrders = reservations.filter((order) => order.phone === phone)
      const totalSpentByUser = userOrders.reduce(
        (sum, order) => sum + (parseFloat(order.totalAmount) || 0),
        0
      )

      // ✅ Calculate total number of orders by this user
      const totalOrdersByUser = userOrders.length

      // ✅ Update selected user data including total orders
      setSelectedUser({
        ...userData,
        totalOrdersByUser,
      })

      setTotalSpent(totalSpentByUser) // Store total spent in state
      setIsModalOpen(true) // Open modal
    } catch (error) {
      console.error("Error fetching user details:", error)
    }
  }

  const fetchUserName = async (phone) => {
    if (!phone) return "Unknown"
    try {
      const usersRef = collection(db, "users")
      const q = query(usersRef, where("phone", "==", phone))
      const querySnapshot = await getDocs(q)

      return !querySnapshot.empty
        ? querySnapshot.docs[0].data().name || "Unknown"
        : "Unknown"
    } catch (error) {
      console.error("Error fetching user name:", error)
      return "Unknown"
    }
  }
  const calculateStatistics = async (reservations) => {
    const totalOrders = reservations.length
    const grossEarnings = reservations.reduce(
      (sum, order) => sum + (parseFloat(order.totalAmount) || 0),
      0
    )

    const avgOrderPrice = totalOrders
      ? (grossEarnings / totalOrders).toFixed(2)
      : 0

    const itemFrequency = {}
    const userOrders = {}
    const userSpending = {}

    reservations.forEach((order) => {
      order.cartItems?.forEach((item) => {
        itemFrequency[item.name] =
          (itemFrequency[item.name] || 0) + item.CartTotalQuantity
      })

      const userPhone =
        order.phone && order.phone.trim() !== "" ? order.phone : "Unknown"
      const orderTotal = parseFloat(order.totalAmount) || 0

      userOrders[userPhone] = (userOrders[userPhone] || 0) + 1
      userSpending[userPhone] = (userSpending[userPhone] || 0) + orderTotal
    })

    // 🔹 Sort users by number of orders, exclude "Unknown"
    const sortedUserOrders = Object.entries(userOrders)
      .filter(([phone]) => phone !== "Unknown")
      .sort((a, b) => b[1] - a[1])

    const maxOrders = sortedUserOrders.length > 0 ? sortedUserOrders[0][1] : 0
    const usersWithMostOrders = sortedUserOrders.filter(
      ([phone, count]) => count === maxOrders
    )
    const userWithMostOrders =
      usersWithMostOrders.length > 0 ? usersWithMostOrders[0][0] : "N/A"

    // 🔹 Sort users by total spending, exclude "Unknown"
    const sortedUserSpending = Object.entries(userSpending)
      .filter(([phone]) => phone !== "Unknown")
      .sort((a, b) => b[1] - a[1])

    const maxSpending =
      sortedUserSpending.length > 0 ? sortedUserSpending[0][1] : 0
    const usersWithHighestSpending = sortedUserSpending.filter(
      ([phone, amount]) => amount === maxSpending
    )
    const userWithHighestSpending =
      usersWithHighestSpending.length > 0
        ? usersWithHighestSpending[0][0]
        : "N/A"

    // 🔍 Fetch user names based on phone numbers
    const userWithMostOrdersName = await fetchUserName(userWithMostOrders)
    const userWithHighestSpendingName = await fetchUserName(
      userWithHighestSpending
    )

    const bestSeller =
      Object.keys(itemFrequency).reduce(
        (a, b) => (itemFrequency[a] > itemFrequency[b] ? a : b),
        ""
      ) || "None"

    return {
      totalOrders,
      avgOrderPrice,
      bestSeller,
      grossEarnings,
      userWithMostOrders,
      userWithHighestSpending,
      userWithMostOrdersName,
      userWithHighestSpendingName,
    }
  }

  const calculatePeakHours = (reservations) => {
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour:
        i === 0
          ? "12 AM"
          : i < 12
          ? `${i} AM`
          : i === 12
          ? "12 PM"
          : `${i - 12} PM`,
      count: 0,
    }))

    reservations.forEach((order) => {
      const orderDate = new Date(order.createdAt?.seconds * 1000 || 0)
      const hour = orderDate.getHours()
      hours[hour].count += 1
    })

    return hours
  }

  useEffect(() => {
    const fetchStatistics = async () => {
      if (reservations) {
        const stats = await calculateStatistics(reservations)
        setStatistics(stats)

        const peakHoursData = calculatePeakHours(reservations)
        setChartOptions({
          data: peakHoursData,
          series: [
            {
              type: "line",
              xKey: "hour",
              yKey: "count",
              marker: { enabled: true, size: 5 },
              tooltip: {
                renderer: ({ datum }) => {
                  return {
                    title: `Hour: ${datum.hour}`,
                    content: `Orders: ${datum.count}`,
                  }
                },
              },
            },
          ],
        })
      }
    }

    fetchStatistics() // Call the async function inside useEffect
  }, [reservations])

  const handleInputClick = (e) => {
    e.preventDefault()
    e.target.showPicker()
  }

  const handleStartDateChange = (e) => setStartDate(e.target.value)
  const handleEndDateChange = (e) => setEndDate(e.target.value)

  if (ordersLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <div className="cont-overview" style={{ direction: "ltr" }}>
      <div className="overview-container">
        <div className="d-flex justify-content-center mb-3">
          <div className="d-flex flex-column mx-2">
            <label htmlFor="start-date" className="mb-2">
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              onClick={handleInputClick}
              className="date-picker"
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="end-date" className="mb-2">
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              onClick={handleInputClick}
              className="date-picker"
            />
          </div>
        </div>

        <div className="stats-overview mt-3">
          <div className="stat-card">
            <h3>Total Orders</h3>
            <p>
              <b> {statistics.totalOrders}</b>
            </p>
          </div>
          <div className="stat-card">
            <h3>Total Earnings</h3>
            <p>
              <b> {statistics.grossEarnings?.toFixed(2)}</b> EGP{" "}
            </p>
          </div>

          <div className="stat-card">
            <h3>Average Order Price</h3>
            <p>
              <b> {statistics.avgOrderPrice} </b>EGP
            </p>
          </div>
          <div className="stat-card">
            <h3>Best Seller</h3>
            <p>
              <b> {statistics.bestSeller}</b>
            </p>
          </div>
          <div className="stat-card">
            <h3>Most Orders</h3>
            <p>
              <b
                onClick={() => handleUserClick(statistics.userWithMostOrders)}
                style={{ cursor: "pointer", color: "blue" }}
              >
                {statistics.userWithMostOrdersName}
              </b>
            </p>
          </div>
          <div className="stat-card">
            <h3>Top Spender</h3>
            <p>
              <b
                style={{ cursor: "pointer", color: "blue" }}
                onClick={() =>
                  handleUserClick(statistics.userWithHighestSpending)
                }
              >
                {statistics.userWithHighestSpendingName}
              </b>
            </p>
          </div>
        </div>

        <div className="chart-section">
          <h3>Peak Hours</h3>
          <div
            className="chart-wrapper"
            style={{
              margin: "0 auto",
              maxWidth: "900px",
              width: "100%",
            }}
          >
            <AgCharts options={chartOptions} />
          </div>
        </div>
      </div>
      {/* User Details Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>User Details</h3>
            {selectedUser ? (
              <>
                <p>
                  <b>Name:</b> {selectedUser.name || "N/A"}
                </p>
                <p>
                  <b>Email:</b> {selectedUser.email || "N/A"}
                </p>
                <p>
                  <b>Phone:</b> {selectedUser.phone || "N/A"}
                </p>
                <p>
                  <b>Total Orders:</b>
                  <span
                    className="mx-1"
                    style={{ color: "green", fontWeight: "bold" }}
                  >
                    {selectedUser.totalOrdersByUser || 0}
                  </span>
                </p>
                <p>
                  <b>Total Spent:</b>
                  <span
                    className="mx-1"
                    style={{ color: "orangered", fontWeight: "bold" }}
                  >
                    {totalSpent.toFixed(2)}
                  </span>
                  EGP
                </p>
                {/* Show total spent */}
              </>
            ) : (
              <p>Loading...</p>
            )}
            <button
              className="close-button"
              onClick={() => setIsModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
